import type { FC } from 'react';
import { lazy } from 'react';
import { useParams } from 'react-router-dom';
import { useMemoOne } from 'use-memo-one';
import {
  useOptionalExchangeRatesCurrent,
  type OptionalExchangeRatesCurrentRequest,
} from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type { ActivitiesParams } from 'src/apis/monolith/types';
import type { ActivitiesRequest } from 'src/apis/monolith/use-activities';
import { useActivities } from 'src/apis/monolith/use-activities';
import type { AuctionRequest } from 'src/apis/monolith/use-auction';
import { useAuction } from 'src/apis/monolith/use-auction';
import type { AuctionConfigRequest } from 'src/apis/monolith/use-auction-config';
import { useAuctionConfig } from 'src/apis/monolith/use-auction-config';
import type { CompetitionsRequest } from 'src/apis/monolith/use-competition-list';
import { useCompetitions } from 'src/apis/monolith/use-competition-list';
import type { OrdersRequest } from 'src/apis/monolith/use-orders';
import { useOrders } from 'src/apis/monolith/use-orders';
import type { UnifiedBidsRequest } from 'src/apis/monolith/use-unified-bids';
import { useUnifiedBids } from 'src/apis/monolith/use-unified-bids';
import type { PortfolioCodesParams } from 'src/apis/transport-auction/types';
import {
  useAssignedBalancingGroups,
  type BalancingGroupsHookParams,
} from 'src/apis/transport-auction/use-assigned-balancing-groups';
import { useAssignedPortfolioCodes } from 'src/apis/transport-auction/use-assigned-portfolio-codes';
import {
  useOptionalTransportAuctionServiceRoot,
  type OptionalTransportAuctionServiceRootRequest,
} from 'src/apis/transport-auction/use-transport-auction-service-root';
import {
  PageSpinner,
  SpinnerContainer,
} from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useTitle } from 'src/hooks/use-title';
import type { PeriodTypeValue } from 'src/pages/reporting/auctions/components/period-type';
import type { AuctionDetailsPageParams } from 'src/pages/transport/auctions/details/use-page-params';
import { useAuctionDetailsPageParams } from 'src/pages/transport/auctions/details/use-page-params';

const Page = lazy(() => import('./page'));

export const AuctionDetailPage: FC = () => {
  // while editing an existing bid the `orderId` is set
  const { auctionId, orderId } = useParams<{
    auctionId: string;
    orderId?: string;
  }>();

  const transportAuctionServiceRoot = useOptionalTransportAuctionServiceRoot();

  const pageParams = useAuctionDetailsPageParams();

  const title = orderId
    ? `Edit Bid for Auction ID ${auctionId}`
    : `Auction ID ${auctionId}`;
  useTitle(title);

  const auction = useAuction({ auctionId });

  const competingAuctions = useCompetitions({ auctionId });
  const auctionConfig = useAuctionConfig({ auctionId, orderId });
  const unifiedBids = useUnifiedBids({ auctionId, onlyIfShipper: true });
  const orders = useOrders({ auctionId, onlyIfShipper: true });
  const exchangeRates = useOptionalExchangeRatesCurrent();

  if (
    transportAuctionServiceRoot?.loaded === false ||
    !auction.response ||
    !competingAuctions.response ||
    !auctionConfig.response ||
    unifiedBids?.loaded === false ||
    exchangeRates.loaded === false ||
    orders?.response === null
  )
    return <PageSpinner />;

  // load activities only for the auction page, not the edit bid page
  return orderId ? (
    <LoadPage
      auction={auction}
      competingAuctions={competingAuctions}
      auctionConfig={auctionConfig}
      unifiedBids={unifiedBids}
      orders={orders}
      title={title}
      orderId={orderId}
      pageParams={pageParams}
      exchangeRates={exchangeRates}
      transportAuctionServiceRoot={transportAuctionServiceRoot}
    />
  ) : (
    <LoadActivities
      auction={auction}
      competingAuctions={competingAuctions}
      auctionConfig={auctionConfig}
      unifiedBids={unifiedBids}
      orders={orders}
      title={title}
      pageParams={pageParams}
      exchangeRates={exchangeRates}
      transportAuctionServiceRoot={transportAuctionServiceRoot}
    />
  );
};

const LoadActivities: FC<{
  auction: Successful<AuctionRequest>;
  competingAuctions: Successful<CompetitionsRequest>;
  auctionConfig: Successful<AuctionConfigRequest>;
  unifiedBids?: UnifiedBidsRequest;
  orders?: Successful<OrdersRequest>;
  title: string;
  pageParams: AuctionDetailsPageParams;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  transportAuctionServiceRoot: OptionalTransportAuctionServiceRootRequest;
}> = ({ auction, ...rest }) => {
  const activitiesParams = useMemoOne<ActivitiesParams>(() => {
    const { data } = auction.response;
    const isShortTerm =
      data.periodType === 'WITHINDAY' || data.periodType === 'DAY';

    const params: ActivitiesParams = {
      phase: data.phase,
      category: isShortTerm
        ? 'SHORT_TERM_AUCTION'
        : !isShortTerm && (data.phase === 'OPEN' || data.phase === 'CLOSED')
          ? 'LONG_TERM_AUCTION'
          : 'COMFORT_BID',
      periodType: data.periodType as PeriodTypeValue,
      cached: false,
    };
    return params;
  }, [auction.response]);
  const activities = useActivities({
    params: activitiesParams,
    onlyIfAuthenticated: true,
  });

  if (activities?.response === null) return <PageSpinner />;

  return <LoadPage auction={auction} activities={activities} {...rest} />;
};

const LoadPage: FC<{
  auction: Successful<AuctionRequest>;
  competingAuctions: Successful<CompetitionsRequest>;
  auctionConfig: Successful<AuctionConfigRequest>;
  unifiedBids?: UnifiedBidsRequest;
  orders?: Successful<OrdersRequest>;
  title: string;
  orderId?: string;
  pageParams: AuctionDetailsPageParams;
  activities?: Successful<ActivitiesRequest>;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  transportAuctionServiceRoot: OptionalTransportAuctionServiceRootRequest;
}> = ({
  auction,
  competingAuctions,
  auctionConfig,
  unifiedBids,
  orders,
  title,
  orderId,
  pageParams,
  activities,
  exchangeRates,
  transportAuctionServiceRoot,
}) => {
  const portfolioCodesParams = useMemoOne<PortfolioCodesParams>(
    () => ({
      networkPointId: auction.response.data.networkPoint.networkPointUuid,
      runtimeStart: auction.response.data.runtime.start,
      runtimeEnd: auction.response.data.runtime.end,
    }),
    [auction.response.data]
  );
  const assignedPortfolioCodes = useAssignedPortfolioCodes({
    root: transportAuctionServiceRoot,
    params: portfolioCodesParams,
    bundled: auction.response.data.networkPoint.bundled,
  });

  const balancingGroupsParams = useMemoOne<BalancingGroupsHookParams>(
    () => ({
      networkPointId: auction.response.data.networkPoint.networkPointUuid,
      runtimeStart: auction.response.data.runtime.start,
      runtimeEnd: auction.response.data.runtime.end,
      capacityCategoryId: auction.response.data.capacityCategory0,
    }),
    [auction.response.data]
  );
  const assignedBalancingGroups = useAssignedBalancingGroups({
    root: transportAuctionServiceRoot,
    params: balancingGroupsParams,
    bundled: auction.response.data.networkPoint.bundled,
  });

  if (
    assignedPortfolioCodes?.response === null ||
    assignedBalancingGroups?.response === null
  )
    return <PageSpinner />;

  // pending state is relevant for competition and recent bid activities as we switch from
  // one details page to another
  const pending =
    auction.pending ||
    Boolean(assignedPortfolioCodes?.pending) ||
    competingAuctions.pending ||
    auctionConfig.pending ||
    Boolean(unifiedBids?.pending) ||
    Boolean(orders?.pending) ||
    Boolean(activities?.pending);

  return (
    <SpinnerContainer pending={pending}>
      <Page
        auction={auction.response.data}
        competingAuctions={competingAuctions.response.data}
        auctionConfig={auctionConfig.response.data}
        unifiedBids={unifiedBids}
        orders={orders?.response.data}
        title={title}
        orderId={orderId}
        pageParams={pageParams}
        activities={activities}
        exchangeRates={exchangeRates}
        assignedPortfolioCodes={assignedPortfolioCodes}
        assignedBalancingGroups={assignedBalancingGroups}
      />
    </SpinnerContainer>
  );
};
